import React from 'react';
import { Box, Skeleton, Stack, TextField, Typography } from '@mui/material';
import MoveToInboxRounded from '@mui/icons-material/MoveToInboxRounded';
import PsychologyRounded from '@mui/icons-material/PsychologyRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import PanToolRounded from '@mui/icons-material/PanToolRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import EditNoteOutlined from '@mui/icons-material/DriveFileRenameOutlineRounded';
import { useNavigate } from 'react-router-dom';
import {
  EmptyState, Modal, Panel, StatusChip, Timeline, ToolbarButton, type TimelineStep,
} from '../../../ui-kit';
import { fetchRequest, sendReply, type PsRequestDetail } from '../../../services/policyServicingService';
import { lengthOf, useLive, when } from '../rocco/live';
import { statusChip } from './status';

const MARKERS: Record<string, React.ReactNode> = {
  received: <MoveToInboxRounded />,
  understood: <PsychologyRounded />,
  policy_found: <PolicyRounded />,
  account_found: <ApartmentRounded />,
  checked: <ManageSearchRounded />,
  answered_records: <FactCheckRounded />,
  records_updated: <EditNoteOutlined />,
  calling: <PhoneInTalkRounded />,
  no_answer: <PhoneInTalkRounded />,
  answered: <FactCheckRounded />,
  drafted: <EditNoteRounded />,
  replied: <SendRounded />,
  needs_person: <PanToolRounded />,
};

const TONES: Record<string, TimelineStep['tone']> = {
  calling: 'brand',
  answered: 'good',
  answered_records: 'good',
  records_updated: 'good',
  replied: 'good',
  needs_person: 'warning',
  no_answer: 'neutral',
};

/**
 * One request, start to finish: the customer's email, what the agent made of
 * it, the policy it found, the call to the carrier, and the reply.
 *
 * The email and the reply sit side by side, so the reader can check one
 * against the other at a glance. A reply held for approval can be read,
 * edited and sent from here. Watches the request while open, so a call that
 * ends while you are reading appears in place.
 */
export default function RequestStoryModal({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const fetcher = React.useCallback(() => fetchRequest(requestId), [requestId]);
  const { data: request, error, reload } = useLive(fetcher);
  const [reading, setReading] = React.useState(false);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const navigate = useNavigate();

  if (reading && request?.call) {
    return <TranscriptModal request={request} onBack={() => setReading(false)} onClose={onClose} />;
  }

  const held = request?.status === 'reply_drafted';
  const canSend = held && request?.source === 'live';
  const send = async () => {
    if (!request) return;
    setSending(true);
    setProblem(null);
    try {
      await sendReply(request.id, editing ?? undefined);
      setEditing(null);
      await reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  const footer = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
      <Typography variant="caption" sx={(t) => ({ flex: 1, color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary })}>
        {problem || (held && !canSend ? 'An example request: its reply cannot be sent.' : '')}
      </Typography>
      {canSend && editing === null ? (
        <ToolbarButton variant="ghost" icon={<EditNoteRounded />} onClick={() => setEditing(request?.reply.body ?? '')}>
          Edit reply
        </ToolbarButton>
      ) : null}
      {canSend ? (
        <ToolbarButton variant="brand" icon={<SendRounded />} onClick={send} loading={sending}
          disabled={editing !== null && !editing.trim()}>
          {editing !== null ? 'Send edited reply' : 'Approve and send'}
        </ToolbarButton>
      ) : null}
      <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
    </Stack>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={request ? (request.subject || request.request_label || 'A request') : 'Loading…'}
      subtitle={request ? [request.email.from_name || request.email.from_email, request.request_label,
        request.employer_name ? [request.member_name, request.employer_name].filter(Boolean).join(' at ')
          : request.policy_num,
        `received ${when(request.received_at)}`].filter(Boolean).join(' · ') : undefined}
      actions={request ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          {request.employer_id ? (
            <ToolbarButton variant="ghost" icon={<ApartmentRounded />}
              onClick={() => { onClose(); navigate(`/agents/policy_servicing/accounts/${request.employer_id}`); }}>
              Open the account
            </ToolbarButton>
          ) : null}
          <StatusChip {...statusChip(request)} />
        </Stack>
      ) : undefined}
      size="lg"
      footer={footer}
    >
      {error && !request ? <EmptyState message={error} /> : null}
      {!request && !error ? <Loading /> : null}
      {request ? (
        <Stack spacing={2}>
          {request.status === 'needs_person' && request.needs_person_reason ? (
            <Box sx={(t) => ({
              p: 1.25, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.semantic.warningSoft,
              color: t.tokens.text.primary,
            })}>
              <Typography variant="body2"><strong>Needs a person:</strong> {request.needs_person_reason}</Typography>
            </Box>
          ) : null}

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
            <Letter
              heading="Their email"
              meta={`${request.email.from_name ? `${request.email.from_name} · ` : ''}${request.email.from_email ?? ''}`}
              body={request.email.body || '(no text)'}
            />
            {editing !== null ? (
              <Stack spacing={0.75}>
                <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>Reply · editing</Typography>
                <TextField
                  id="ps-reply-body"
                  value={editing}
                  onChange={(e) => setEditing(e.target.value)}
                  inputProps={{ maxLength: 10000, 'aria-label': 'Reply' }}
                  multiline
                  minRows={10}
                  fullWidth
                  size="small"
                />
              </Stack>
            ) : request.reply.body ? (
              <Letter
                heading={request.reply.sent_at ? 'Our reply' : 'Our reply · waiting for approval'}
                meta={request.reply.sent_at ? `Sent ${when(request.reply.sent_at)}` : 'Not sent yet'}
                body={request.reply.body}
                tone="brand"
              />
            ) : (
              <Letter
                heading="Our reply"
                meta=""
                body={request.status === 'needs_person'
                  ? 'No reply was sent. A person picks this one up.'
                  : request.employer_id ? 'Written once our records have been checked, or the carrier has answered.'
                    : 'Written once the carrier has answered.'}
                muted
              />
            )}
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
            <Panel title="What happened" subtitle="Each step, as it happened">
              <Timeline steps={toSteps(request, () => setReading(true))} />
            </Panel>
            <Stack spacing={2}>
              {request.question ? (
                <Panel title="What they asked" subtitle="Read from the email into a fixed form">
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{request.question}</Typography>
                </Panel>
              ) : null}
              {request.checked?.length ? (
                <Panel title="What our records say" subtitle={request.checked_why ?? undefined}>
                  <Facts rows={request.checked} />
                </Panel>
              ) : null}
              {request.updates?.length ? (
                <Panel title="Written back to the record" subtitle="What the carrier said, now on the employee's record">
                  <Stack spacing={1}>
                    {request.updates.map((u) => (
                      <Stack key={`${u.record}-${u.field}`} spacing={0.25}>
                        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                          {u.record} · {u.field}
                        </Typography>
                        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
                          <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>{u.before} → </Box>
                          {u.after}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Panel>
              ) : null}
              {request.next_action ? (
                <Panel title="Next action">
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{request.next_action}</Typography>
                </Panel>
              ) : null}
              <Panel title="What the carrier was told" subtitle="Only these facts, from our records">
                {request.facts.length ? <Facts rows={request.facts} /> : (
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                    {request.answered_by === 'records'
                      ? 'Nothing: our records answered this, so no call was needed.'
                      : 'Nothing: the carrier was not called.'}
                  </Typography>
                )}
              </Panel>
            </Stack>
          </Box>
        </Stack>
      ) : null}
    </Modal>
  );
}

/** Labels on the left, their values on the right. Both wrap. */
function Facts({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <Stack spacing={0.75}>
      {rows.map((fact) => (
        <Stack key={fact.label} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fact.label}</Typography>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right' })}>
            {fact.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function toSteps(request: PsRequestDetail, openCall: () => void): TimelineStep[] {
  return request.steps.map((step, index) => ({
    key: `${step.kind}-${index}`,
    title: step.title,
    meta: when(step.at),
    detail: step.detail ?? undefined,
    marker: MARKERS[step.kind] ?? <FactCheckRounded />,
    tone: TONES[step.kind],
    // The transcript belongs with the call that produced it: the answer.
    action: step.kind === 'answered' && request.call?.transcript.length ? (
      <ToolbarButton variant="ghost" onClick={openCall}>Read the call</ToolbarButton>
    ) : undefined,
  }));
}

/** An email, set as a letter: who and when above, the words below. */
function Letter({
  heading, meta, body, tone, muted = false,
}: { heading: string; meta: string; body: string; tone?: 'brand'; muted?: boolean }) {
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="overline" sx={(t) => ({ color: tone ? t.tokens.brand.primary : t.tokens.text.tertiary })}>
          {heading}
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{meta}</Typography>
      </Stack>
      <Typography
        variant="body2"
        sx={(t) => ({
          p: 1.5,
          minHeight: 160,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          borderRadius: `${t.ds.radius.md}px`,
          border: `1px solid ${tone ? t.tokens.brand.primary : t.tokens.border.subtle}`,
          bgcolor: tone ? t.tokens.brand.primarySoft : t.tokens.surface.sunken,
          color: muted ? t.tokens.text.tertiary : t.tokens.text.primary,
        })}
      >
        {body}
      </Typography>
    </Stack>
  );
}

function Loading() {
  return (
    <Stack spacing={1.25} aria-hidden>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Skeleton variant="rounded" height={180} />
        <Skeleton variant="rounded" height={180} />
      </Box>
      <Skeleton variant="rounded" height={220} />
    </Stack>
  );
}

function TranscriptModal({
  request, onBack, onClose,
}: { request: PsRequestDetail; onBack: () => void; onClose: () => void }) {
  const call = request.call!;
  const rep = request.outcome.rep_name || request.carrier || 'Carrier';
  return (
    <Modal
      open
      onClose={onClose}
      title={`Call to ${request.carrier || 'the carrier'}`}
      subtitle={[request.member_name ? `${request.member_name} · ${request.employer_name}` : request.policy_num,
        lengthOf(call.duration_secs),
        request.outcome.reference_number && `reference ${request.outcome.reference_number}`].filter(Boolean).join(' · ')}
      size="lg"
      footer={
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          <ToolbarButton variant="ghost" onClick={onBack} icon={<ArrowBackRounded />}>Back to the request</ToolbarButton>
          <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        {call.summary ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{call.summary}</Typography>
        ) : null}
        <Stack spacing={1.25} sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}>
          {call.transcript.map((turn, i) => (
            <Stack key={i} spacing={0.25}>
              <Typography variant="overline"
                sx={(t) => ({ color: turn.role === 'agent' ? t.tokens.brand.primary : t.tokens.text.tertiary })}>
                {turn.role === 'agent' ? 'Lina · AI' : rep}
              </Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{turn.message}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Modal>
  );
}
